import React, { FC, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAppDispatch, useAppSelector } from "store/hooks/hooks";
import {
    fetchProductsByEvent,
    selectEventProductsStatus,
    selectEventProducts,
    selectCurrentEventPage,
    selectEventHasReachedEnd,
    setCurrentEventPage,
    clearEventProducts,
} from "store/slices/eventsSlice";
import ProductCard from "@/components/products/ProductCard";
import { addToCart } from "store/slices/cartSlice";
import infiniteLoader from "../../assets/loaders/InfinityLoader.gif";
import ToTitleCase from "@/utils/ToTitleCase";

const OccasionalGiftingPage: FC = () => {
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const eventId = searchParams.get("eventId");
    const eventName = searchParams.get("eventName") || "Occasion";
    const targetEventId = eventId || "";

    const status = useAppSelector(selectEventProductsStatus);
    const currentPage = useAppSelector(selectCurrentEventPage);
    const hasReachedEnd = useAppSelector(selectEventHasReachedEnd);
    const eventProducts = useAppSelector(selectEventProducts);

    const observerRef = useRef<IntersectionObserver | null>(null);
    const sentinelRef = useRef<HTMLDivElement | null>(null);
    const isFetchingRef = useRef(false);

    // Initial Fetch & Cleanup
    useEffect(() => {
        window.scrollTo(0, 0);

        if (targetEventId) {
            // Always fetch fresh data on mount
            dispatch(fetchProductsByEvent({ eventId: targetEventId, page: 1 }));
        }

        return () => {
            // Clear products when leaving the page to prevent stale data
            dispatch(clearEventProducts());
        };
    }, [dispatch, targetEventId]);

    const fetchMoreProducts = useCallback(() => {
        if (isFetchingRef.current || hasReachedEnd || !targetEventId) return;

        isFetchingRef.current = true;
        const nextPage = currentPage + 1;

        dispatch(setCurrentEventPage(nextPage));
        dispatch(fetchProductsByEvent({ eventId: targetEventId, page: nextPage }))
            .finally(() => {
                isFetchingRef.current = false;
            });
    }, [dispatch, targetEventId, currentPage, hasReachedEnd]);

    useEffect(() => {
        if (observerRef.current) observerRef.current.disconnect();

        observerRef.current = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && !isFetchingRef.current && !hasReachedEnd) {
                fetchMoreProducts();
            }
        }, { rootMargin: "400px" });

        if (sentinelRef.current) observerRef.current.observe(sentinelRef.current);

        return () => observerRef.current?.disconnect();
    }, [fetchMoreProducts, hasReachedEnd]);

    return (
        <div className="nc-OccasionalGiftingPage bg-white">
            <Helmet>
                <title>{eventName} Collections || 360CUSTOMIZER</title>
            </Helmet>

            {/* Product Listing Section */}
            <div className="container pt-2 pb-24">
                <div className="flex flex-col items-center justify-center py-4 md:py-4 px-4 text-center">
                    <div className="container mx-auto max-w-6xl">
                        <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-full px-6 py-2 mb-8 shadow-sm">
                            <span className="text-blue-600 text-xs font-bold uppercase tracking-[0.2em]">Event Picks</span>
                        </div>

                        <h1 className="text-2xl md:text-4xl lg:text-6xl font-bold mb-8 leading-tight font-inter">
                            <span className="text-primary-gradient block line-height-3">
                                <ToTitleCase text={eventName}></ToTitleCase>  Specials
                            </span>
                        </h1>

                        <p className="text-lg md:text-xl text-gray-600/80 mb-0 max-w-4xl mx-auto leading-relaxed font-inter">
                            Discover our curated selection of premium gifts, perfectly tailored for <span className="text-gray-900 font-bold underline decoration-blue-500 underline-offset-8"> {eventName}</span>.
                        </p>
                    </div>
                </div>

                {status === "loading" && eventProducts.length === 0 ? (
                    <div className="flex justify-center items-center">
                        <img src={infiniteLoader} alt="loader" className="w-1/5" />
                    </div>
                ) : eventProducts.length > 0 ? (
                    <>
                        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-4 gap-y-8 sm:gap-x-6 sm:gap-y-12">
                            {eventProducts.map((product) => (
                                <ProductCard
                                    key={product.id}
                                    data={product}
                                    dispatch={dispatch}
                                    addToCart={addToCart}
                                />
                            ))}
                        </div>

                        {/* Sentinel for Infinite Scroll */}
                        <div ref={sentinelRef} className="h-20 w-full flex justify-center items-center mt-12">
                            {status === "loading" && (
                                <img src={infiniteLoader} alt="Loading more..." className="w-12 h-12" />
                            )}
                        </div>
                    </>
                ) : (
                    <div className="text-center py-32 bg-gray-50/50 rounded-3xl border-2 border-dashed border-gray-100">
                        <div className="text-6xl mb-6">🎁</div>
                        <h3 className="text-2xl font-black text-gray-900 mb-2 uppercase tracking-tight">No items discovered yet</h3>
                        <p className="text-gray-400 font-medium font-outfit">We're updating our collection for {eventName}. Please check back soon.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default OccasionalGiftingPage;
